import { useSupabaseUser, navigateTo } from '#imports'

// Session-expiry helpers.
//
// When the JWT + refresh token die mid-session (e.g. the user left a panel open
// idle past the token TTL and the refresh-token grant returned 400), supabase-js
// clears the session. Any in-flight or subsequent write then goes out with only
// the anon apikey — no user JWT. PostgREST answers 200 but RLS matches 0 rows,
// so `assertAffected` would throw the caller's generic domain message
// ("Verifica tus permisos o que la cita siga existiendo"), which is misleading:
// the row exists and permissions are fine — the session is simply dead.
//
// These helpers let the write path distinguish "no live session" from a real
// RLS/permission denial, surface an honest message, and send the user to login.
// Complements the silent-failure guard (assertAffected / G-021) and the
// session-heartbeat plugin's 401 → refresh/redirect handling.

export class SessionExpiredError extends Error {
  readonly sessionExpired = true
  constructor(message = 'Tu sesión expiró. Inicia sesión de nuevo.') {
    super(message)
    this.name = 'SessionExpiredError'
  }
}

// True only when we can CONFIRM there is no live Supabase user on the client.
// Conservative by design: returns false (assume the session is alive) during SSR
// or when the auth state can't be read, so a genuine RLS/permission error is
// never mislabelled as a session expiry.
export function isSessionDead(): boolean {
  if (typeof window === 'undefined') return false // SSR — assume alive
  try {
    return !useSupabaseUser().value
  } catch {
    return false // outside Nuxt runtime (e.g. a bare unit) — assume alive
  }
}

// Send the user to login flagged as "session expired". Mirrors the
// session-heartbeat plugin: the sessionStorage flag survives the
// @nuxtjs/supabase redirect that strips query params, so the login page can
// still show the "Tu sesión expiró" toast. Idempotent — safe to call repeatedly
// and a no-op once already on the login route.
export function redirectToLoginExpired(): void {
  if (typeof window === 'undefined') return
  try {
    sessionStorage.setItem('auth_redirect_reason', 'session_expired')
  } catch {
    /* sessionStorage unavailable (private mode quota) — ignore */
  }
  if (!window.location.pathname.startsWith('/auth/login')) {
    void navigateTo('/auth/login?reason=session_expired')
  }
}
